import React, { useState, useEffect, useCallback, useRef } from 'react';

interface SearchParams {
  searchTerm: string;
  userLevel?: number;
  status?: string;
}

interface UserSearchFormProps {
  onSearch: (params: SearchParams) => void;
}

const UserSearchForm: React.FC<UserSearchFormProps> = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [userLevel, setUserLevel] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounced search function
  const debouncedSearch = useCallback((params: SearchParams) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      onSearch(params);
    }, 300); // 300ms debounce
  }, [onSearch]);

  // 검색어 변경 시 자동 검색 (디바운싱 적용)
  useEffect(() => {
    debouncedSearch({ searchTerm, userLevel, status });
  }, [searchTerm, userLevel, status, debouncedSearch]);

  // 컴포넌트 언마운트 시 디바운싱 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleUserLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) {
      setUserLevel(undefined);
      return;
    }

    const parsed = Number(value);
    setUserLevel(Number.isFinite(parsed) ? parsed : undefined);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 디바운싱 취소 후 즉시 검색
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    onSearch({ searchTerm, userLevel, status });
  };

  const handleReset = () => {
    setSearchTerm('');
    setUserLevel(undefined);
    setStatus('');
    // 디바운싱 취소 후 즉시 초기화
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    onSearch({ searchTerm: '', userLevel: undefined, status: '' });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col space-y-6">
          {/* 검색어 입력 */}
          <div className="flex space-x-4">
            <div className="flex-grow">
              <label htmlFor="searchTerm" className="block text-sm font-medium text-[#1e2c49] mb-2">
                검색어
              </label>
              <input
                type="text"
                id="searchTerm"
                name="searchTerm"
                value={searchTerm}
                onChange={handleSearchTermChange}
                className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
                placeholder="이름, 이메일, 소속, 전화번호로 검색"
              />
            </div>

            <div className="flex space-x-2 items-end">
              <button
                type="button"
                onClick={handleReset}
                className="w-[100px] h-12 rounded-sm border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] hover:bg-gray-50 transition-colors"
              >
                초기화
              </button>
              <button
                type="submit"
                className="w-[100px] h-12 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
              >
                검색
              </button>
            </div>
          </div>

          {/* 필터 옵션 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="userLevel" className="block text-sm font-medium text-[#1e2c49] mb-2">
                권한 필터
              </label>
              <select
                id="userLevel"
                name="userLevel"
                value={userLevel ?? ''}
                onChange={handleUserLevelChange}
                className="w-full h-10 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 text-[#1e2c49] text-base font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              >
                <option value="">모든 권한</option>
                <option value="1">최고 관리자</option>
                <option value="2">일반 관리자</option>
                <option value="3">공사 관계자</option>
                <option value="4">센터 관계자</option>
                <option value="5">입주업체</option>
                <option value="6">입주협력업체</option>
                <option value="7">공공기관</option>
                <option value="8">항만근로자</option>
                <option value="9">일반 사용자</option>
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-[#1e2c49] mb-2">
                상태 필터
              </label>
              <select
                id="status"
                name="status"
                value={status}
                onChange={handleStatusChange}
                className="w-full h-10 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-3 text-[#1e2c49] text-base font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              >
                <option value="">모든 상태</option>
                <option value="active">활성</option>
                <option value="inactive">비활성</option>
                <option value="suspended">일시정지</option>
                <option value="blocked">차단됨</option>
              </select>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-blue-800 text-sm font-medium mb-1">자동 검색 기능</p>
                <p className="text-blue-700 text-xs">
                  검색어나 필터를 변경하면 자동으로 검색됩니다. 검색 버튼을 누르면 즉시 검색됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserSearchForm; 
